import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Loader2, Mail, ArrowLeft, User, Shield, Wrench, Download, Smartphone, Share, Plus, MoreVertical, X } from 'lucide-react';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('admin');
  
  // OTP states for customer
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [customerLoginMethod, setCustomerLoginMethod] = useState('otp'); // 'otp' or 'password'
  const [isMobile] = useState(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod|android|mobile/.test(ua) || window.innerWidth < 768;
  });
  const [isIOS] = useState(() => /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase()));
  const [isAndroid] = useState(() => /android/.test(window.navigator.userAgent.toLowerCase()));
  const [isStandalone] = useState(() =>
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  );
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false);

  // PWA install prompt listener
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') toast.success('App installed successfully!');
    setDeferredPrompt(null);
    setInstallPromptDismissed(true);
  };

  // OTP Timer countdown
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Reset OTP state when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setOtpSent(false);
    setOtp('');
    setEmail('');
    setPassword('');
    setCustomerLoginMethod('otp');
  };

  // Admin/Handyman/Customer password login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Store tokens
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('pwa_token', response.data.token);
      localStorage.setItem('pwa_user', JSON.stringify(response.data.user));
      
      toast.success('Login successful!');
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Request OTP for customer login
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/customer/request-otp`, { email });
      setOtpSent(true);
      setOtpTimer(60);
      toast.success('OTP sent to your email!');
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'Failed to send OTP';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and login customer
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/customer/verify-otp`, {
        email,
        otp_code: otp
      });

      const { token, user, customer } = response.data;

      // Store session
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('pwa_token', token);
      localStorage.setItem('pwa_user', JSON.stringify(user));
      localStorage.setItem('customer', JSON.stringify(customer));

      toast.success(`Welcome, ${user.name}!`);
      onLogin(user, token);
    } catch (error) {
      // Handle validation errors (Pydantic)
      const errorData = error.response?.data;
      let errorMessage = 'Invalid OTP';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        // Pydantic validation errors
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      } else if (typeof errorData?.detail === 'object') {
        errorMessage = errorData.detail.msg || errorData.detail.message || 'Validation error';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (otpTimer > 0) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/customer/resend-otp`, { email });
      setOtpTimer(60);
      toast.success('OTP resent to your email!');
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'Failed to resend OTP';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const showMobileInstallBanner = isMobile && !isStandalone && !installPromptDismissed;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: '#EFF7FF' }}>
      {/* Logo */}
      <div className="mb-6 text-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_1dafeb02-9ec2-4436-8d6b-af3019fabee1/artifacts/je75u1q2_White%20logo%20thick%20copy.jpg" 
          alt="Redefyne Logo" 
          className="w-48 h-auto mx-auto mb-3 rounded-2xl shadow-xl" 
        />
      </div>

      {/* Mobile PWA Install Prompt */}
      {showMobileInstallBanner && (
        <div data-testid="pwa-install-banner" className="w-full max-w-md mb-4">
          <Card className="bg-[#3a3a3a] border-0 shadow-2xl rounded-2xl overflow-hidden">
            <div className="relative">
              <button
                data-testid="pwa-install-dismiss"
                onClick={() => setInstallPromptDismissed(true)}
                className="absolute top-3 right-3 text-white/50 hover:text-white/90 transition-colors z-10"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
              <CardContent className="pt-5 pb-4 px-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Install Redefyne App</p>
                    <p className="text-white/60 text-xs">Quick access from your home screen</p>
                  </div>
                </div>

                {(isAndroid || (!isIOS && !isAndroid)) && deferredPrompt && (
                  <Button
                    data-testid="pwa-install-button"
                    onClick={handleInstallClick}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white h-10 text-sm font-medium rounded-xl"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Install Now
                  </Button>
                )}

                {(isAndroid || (!isIOS && !isAndroid)) && !deferredPrompt && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">1</span>
                      </div>
                      <p className="text-xs">Tap <MoreVertical className="w-3.5 h-3.5 inline -mt-0.5" /> menu in your browser</p>
                    </div>
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">2</span>
                      </div>
                      <p className="text-xs">Select "Add to Home Screen"</p>
                    </div>
                  </div>
                )}

                {isIOS && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">1</span>
                      </div>
                      <p className="text-xs">Tap <Share className="w-3.5 h-3.5 inline -mt-0.5" /> Share at the bottom</p>
                    </div>
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">2</span>
                      </div>
                      <p className="text-xs">Tap <Plus className="w-3.5 h-3.5 inline -mt-0.5 border rounded-sm border-white/40" /> "Add to Home Screen"</p>
                    </div>
                    <p className="text-white/40 text-[10px] mt-1 pl-8">Use Safari for best experience</p>
                  </div>
                )}

                <button
                  data-testid="pwa-install-skip"
                  onClick={() => setInstallPromptDismissed(true)}
                  className="w-full text-center text-white/40 hover:text-white/60 text-xs mt-3 transition-colors"
                >
                  Skip, continue to login
                </button>
              </CardContent>
            </div>
          </Card>
        </div>
      )}

      <div className="w-full max-w-md">

        <Card className="backdrop-blur-sm bg-white/80 shadow-xl border-0">
          <CardHeader>
            <CardTitle>Welcome Back</CardTitle>
            <CardDescription>Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="admin" data-testid="admin-tab" className="flex items-center gap-1">
                  <Shield size={14} />
                  Admin
                </TabsTrigger>
                <TabsTrigger value="customer" data-testid="customer-tab" className="flex items-center gap-1">
                  <User size={14} />
                  Customer
                </TabsTrigger>
                <TabsTrigger value="handyman" data-testid="handyman-tab" className="flex items-center gap-1">
                  <Wrench size={14} />
                  Handyman
                </TabsTrigger>
              </TabsList>

              {/* Admin Tab - Password Login */}
              <TabsContent value="admin">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email</Label>
                    <Input
                      id="admin-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="admin@servicepro.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-password">Password</Label>
                    <Input
                      id="admin-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-btn">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <Shield className="mr-2 h-4 w-4" />
                        Sign In as Admin
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Customer Tab - OTP or Password Login */}
              <TabsContent value="customer">
                {/* Login Method Toggle */}
                <div className="flex gap-2 mb-4 p-1 bg-gray-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => { setCustomerLoginMethod('otp'); setOtpSent(false); setPassword(''); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      customerLoginMethod === 'otp' 
                        ? 'bg-white shadow text-gray-900' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Mail className="inline w-4 h-4 mr-1" />
                    OTP Login
                  </button>
                  <button
                    type="button"
                    onClick={() => { setCustomerLoginMethod('password'); setOtpSent(false); setOtp(''); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      customerLoginMethod === 'password' 
                        ? 'bg-white shadow text-gray-900' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Shield className="inline w-4 h-4 mr-1" />
                    Password
                  </button>
                </div>

                {customerLoginMethod === 'password' ? (
                  // Password Login for Customer
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="customer-email-pwd">Email</Label>
                      <Input
                        id="customer-email-pwd"
                        data-testid="customer-login-email-pwd"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="customer-password">Password</Label>
                      <Input
                        id="customer-password"
                        data-testid="customer-login-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-11"
                      />
                    </div>
                    <Button type="submit" className="w-full h-11" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          <User className="mr-2 h-4 w-4" />
                          Sign In
                        </>
                      )}
                    </Button>
                  </form>
                ) : !otpSent ? (
                  // Step 1: Enter Email for OTP
                  <form onSubmit={handleRequestOtp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="customer-email">Email</Label>
                      <Input
                        id="customer-email"
                        data-testid="customer-login-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="h-11"
                      />
                    </div>
                    <Button type="submit" className="w-full h-11" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending OTP...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Send OTP
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      We'll send a 6-digit verification code to your email
                    </p>
                  </form>
                ) : (
                  // Step 2: Enter OTP
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div className="text-center mb-2">
                      <p className="text-sm text-gray-600">
                        OTP sent to <strong>{email}</strong>
                      </p>
                      <button
                        type="button"
                        onClick={() => { setOtpSent(false); setOtp(''); }}
                        className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1 mt-1 mx-auto"
                      >
                        <ArrowLeft size={14} />
                        Change email
                      </button>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="otp">Enter OTP</Label>
                      <Input
                        id="otp"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        className="h-14 text-2xl text-center tracking-widest font-mono"
                        maxLength={6}
                        autoComplete="one-time-code"
                      />
                    </div>
                    <Button type="submit" className="w-full h-11" disabled={loading || otp.length !== 6}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <User className="mr-2 h-4 w-4" />
                          Verify & Sign In
                        </>
                      )}
                    </Button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={otpTimer > 0 || loading}
                        className={`text-sm ${otpTimer > 0 ? 'text-gray-400' : 'text-blue-600 hover:underline'}`}
                      >
                        {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>

              {/* Handyman Tab - Password Login */}
              <TabsContent value="handyman">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="handyman-email">Email</Label>
                    <Input
                      id="handyman-email"
                      data-testid="handyman-login-email-input"
                      type="email"
                      placeholder="mike@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="handyman-password">Password</Label>
                    <Input
                      id="handyman-password"
                      data-testid="handyman-login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <Wrench className="mr-2 h-4 w-4" />
                        Sign In as Handyman
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="mt-6 text-center text-gray-500 text-xs space-y-1">
        <p className="font-medium">2301 S Stearman Dr, Chandler, AZ 85286</p>
        <p>+1 602-715-1313 | support@redefyneaz.com</p>
        <p className="text-gray-400 mt-2">&copy; 2025 Redefyne Interior & Exterior</p>
      </div>
    </div>
  );
}
